/** GMAPS_SHA1_FOLLOWUP control: after the AAB is on Play, paste the Play app-signing SHA-1 to
 *  restrict the Maps key with it (replacing the debug SHA-1). Gated until the Maps key exists.
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { GmapsSha1Detail, PreflightItem } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { copyOnlyActions } from '../tenantLinks';

export function GmapsSha1Control({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const detail = item.detail as unknown as GmapsSha1Detail;
  const [sha1, setSha1] = useState('');
  const [opError, setOpError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => items.saveGmapsSha1(topId, item.id, { sha1: sha1.trim() }),
    onMutate: () => setOpError(null),
    onSuccess: (res) => {
      if (res.ok) {
        snackbar.show('Play SHA-1 applied to the Maps key.', 'success');
        setSha1('');
        onChanged();
      } else {
        setOpError(typeof res.detail === 'string' ? res.detail : null);
        snackbar.show(
          res.reason === 'no_key' ? 'Set up the Maps SDK key first.' : 'Could not apply the SHA-1.',
          'error',
        );
      }
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not apply the SHA-1.', 'error'),
  });

  if (detail?.configured === false) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Alert severity="info">Google isn&apos;t configured on the server.</Alert>
      </Box>
    );
  }
  if (detail?.gate_reason) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Alert severity="info">Blocked: {detail.gate_reason}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1.5}>
        {opError && (
          <Alert severity="error" onClose={() => setOpError(null)} sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {opError}
          </Alert>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            columnGap: 1.5,
            rowGap: 0.25,
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Debug SHA-1
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            <ValueActions actions={copyOnlyActions(detail?.debug_sha1, 'Copy debug SHA-1')}>
              {detail?.debug_sha1 || '—'}
            </ValueActions>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Play SHA-1
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {detail?.play_sha1 ? (
              <CheckCircleIcon fontSize="small" color="success" />
            ) : (
              <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
            )}
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              <ValueActions actions={copyOnlyActions(detail?.play_sha1, 'Copy Play SHA-1')}>
                {detail?.play_sha1 || '(not applied)'}
              </ValueActions>
            </Typography>
          </Stack>
        </Box>

        {canManage && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              label="Play app-signing SHA-1"
              value={sha1}
              onChange={(e) => setSha1(e.target.value)}
              disabled={save.isPending}
              sx={{ flex: 1, minWidth: 280, maxWidth: 480 }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={() => save.mutate()}
              disabled={!sha1.trim() || save.isPending}
              startIcon={save.isPending ? <CircularProgress size={14} /> : undefined}
            >
              Apply SHA-1
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
